// ============================================================================
// AudioAttachment — голосовое/аудио-вложение: ЕДИНЫЙ SSoT проигрыватель
// (components/media/MediaPlayer — тот же, что в просмотре файлов).
// Плеер монтируется ЛЕНИВО (§2.4): пока вложение далеко от вьюпорта — лёгкая
// заглушка (кнопка play + скелет-волна, ничего не загружается); автозапуск
// очереди «слушать подряд» монтирует и проигрывает принудительно (необратимо).
// Кнопка «Текст» — ЛИЧНАЯ транскрибация (STT через ExternalAPI whisper):
// распознанный текст показывается ТОЛЬКО у нажавшего (другая сторона не видит).
// Метки приходят в ответе сервера — панель делает слова кликабельными
// (клик = перемотка на слово) с подсветкой текущего слова, а фразовые
// сегменты — «главами» над словами; меток нет — текст без кликов.
// Вынесено из MessageBubble (SRP; бюджет строк файла бабла).
// ============================================================================

import { useState } from 'react';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { parseTranscriptWords, type TranscriptWordData } from '@/lib/transcriptWords';
import { parseTranscriptChapters, type TranscriptChapterData } from '@/lib/transcriptChapters';
import { useNearViewport } from '@/hooks/useNearViewport';
import { MediaPlayer } from '@/components/media/MediaPlayer';
import { MediaPlaceholder } from '@/components/media/MediaPlaceholder';
import { TranscriptPanel } from '@/components/media/TranscriptPanel';

export function AudioAttachment({ id, name, mine, voice = false, onDownload, autoPlay, onEnded, onInterrupt, onAutoplayConsumed }: {
  id: string;
  name: string;
  mine: boolean;
  /** Голосовое сообщение (запись чата) — отдельный компактный вид плеера. */
  voice?: boolean;
  onDownload: (id: string, name: string) => void;
  /** «Слушать подряд»: очередь просит автозапуск этого аудио */
  autoPlay?: boolean;
  /** Конец воспроизведения (очередь запросит следующее аудио ленты) */
  onEnded?: () => void;
  /** Ручная пауза/перемотка — отмена автозапуска */
  onInterrupt?: () => void;
  /** Автозапуск состоялся — очередь сбрасывает цель */
  onAutoplayConsumed?: () => void;
}) {
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [words, setWords] = useState<TranscriptWordData[]>([]);
  const [chapters, setChapters] = useState<TranscriptChapterData[]>([]);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);
  // Клик по заглушке — «воспроизвести»: монтирует плеер и просит автозапуск.
  const [playRequested, setPlayRequested] = useState(false);
  // Автозапуск очереди состоялся — плеер остаётся смонтированным: цель
  // очереди сразу сбрасывается (autoPlay=false), воспроизведение прерывать
  // нельзя (необратимое условие рядом с кликом и близостью к вьюпорту).
  const [keepAlive, setKeepAlive] = useState(false);
  const { ref, near } = useNearViewport();
  const active = near || playRequested || keepAlive || !!autoPlay;

  const transcribe = async () => {
    setTranscribing(true);
    setTranscribeError(null);
    try {
      const r = await getSDK().externalApi.transcribeAudio({ fileId: id });
      setTranscript(r.text);
      setWords(parseTranscriptWords(r.words));
      // Сегменты-главы: сервер отдаёт их, когда провайдер дал фразовые метки;
      // пусто/нет поля — глав не будет (фейковые не создаются).
      setChapters(parseTranscriptChapters(r));
    } catch (e: unknown) {
      setTranscribeError(friendlyError(e));
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div ref={ref}>
      {active ? (
        <MediaPlayer
          fileId={id}
          kind="audio"
          variant={voice ? 'voice' : 'audio'}
          name={name}
          mine={mine}
          compact
          autoPlay={autoPlay || playRequested}
          onEnded={onEnded}
          onInterrupt={onInterrupt}
          onAutoplayConsumed={() => { setKeepAlive(true); onAutoplayConsumed?.(); }}
          onDownload={() => onDownload(id, name)}
          footer={transcript === null ? (
            <>
              <div className="mt-1 flex items-center gap-2 border-t pt-1">
                <button
                  type="button"
                  className="shrink-0 text-[10px] text-primary underline disabled:opacity-50"
                  title="Распознать текст (только для вас; другая сторона не увидит)"
                  disabled={transcribing}
                  onClick={() => void transcribe()}
                >
                  {transcribing ? 'Распознаю…' : 'Текст'}
                </button>
                <span className="text-[10px] text-muted-foreground">распознавание — только у вас</span>
              </div>
              {transcribeError && <div className="mt-1 text-[10px] text-destructive">{transcribeError}</div>}
            </>
          ) : (
            <TranscriptPanel text={transcript} words={words} chapters={chapters} />
          )}
        />
      ) : (
        <MediaPlaceholder
          kind="audio"
          name={name}
          mine={mine}
          label={voice ? 'Голосовое сообщение' : name}
          onPlay={() => setPlayRequested(true)}
        />
      )}
    </div>
  );
}
